// src/client/services/creditScoreService.ts
import { ok, err, type ServiceResult } from '@/lib/types';
import { supabase } from '@client/lib/supabase';
import type { CreditScoreFixture, CreditFactor } from '@client/lib/fixtures';
import type { CreditTier } from '@client/lib/creditScore.types';

export interface CreditScoreEvent {
  id: string;
  occurred_at: string;
  delta: number;
  reason: string;
}

const TIER_LIMIT_NGWEE: Record<CreditTier, number> = {
  new: 50_000,
  building: 150_000,
  established: 500_000,
  strong: 1_000_000,
  excellent: 2_000_000,
};

interface BreakdownJson {
  factors?: CreditFactor[];
}

export async function getMyScore(): Promise<ServiceResult<CreditScoreFixture>> {
  const { data, error } = await supabase
    .from('credit_scores')
    .select('user_id, current_score, current_tier, breakdown_jsonb, updated_at')
    .maybeSingle();
  if (error) return err('internal_error');
  if (!data) return err('no_score_yet');

  const tier = data.current_tier as CreditTier;
  const breakdown = (data.breakdown_jsonb as BreakdownJson | null) ?? {};
  const factors = Array.isArray(breakdown.factors) ? breakdown.factors : [];

  const fixture: CreditScoreFixture = {
    user_id: (data.user_id as string | null) ?? '',
    score: data.current_score as number,
    tier,
    max_loan_ngwee: TIER_LIMIT_NGWEE[tier],
    factors,
    updated_at: data.updated_at as string,
  };
  return ok(fixture);
}

export async function getScoreHistory(): Promise<ServiceResult<CreditScoreEvent[]>> {
  const { data, error } = await supabase
    .from('credit_score_events')
    .select('id, created_at, delta, reason')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return err('internal_error');
  const events: CreditScoreEvent[] = (data ?? []).map((row) => ({
    id: row.id as string,
    occurred_at: row.created_at as string,
    delta: row.delta as number,
    reason: row.reason as string,
  }));
  return ok(events);
}
